const BLOCKS = ':is(p, ul, ol, blockquote, pre, table, hr, h1, h2, h3, h4, h5, h6, div)'

/**
 * Space between consecutive blocks for presets that set `blockGap` (ugc, request, chat-message),
 * so a blank line in the markdown shows as a gap and not as an injected `<br>`; an email client
 * spaces paragraphs about the same. It matches top-level blocks by tag, so
 * the editor (whose Slate blocks are the same tags) spaces paragraphs exactly like the bubble. It is
 * a sibling rule: markdown rendered as one `MarkdownRenderer` per block needs its container to add
 * the same gap.
 */
export const CHAT_BLOCK_GAP = '1em'

export const typographyStyles = (overrides: any = {}, options: any = null) => ({
  // Top-level blocks only (the renderer root's children, or the editor's), never inside an embed
  ...(overrides?.blockGap
    ? {
        [`& > ${BLOCKS} + ${BLOCKS}, & [data-slate-editor] > ${BLOCKS} + ${BLOCKS}`]: {
          marginTop: overrides.blockGap,
        },
        // The gap is the only space between blocks, so a list or quote at either end adds none
        '& > :first-child, & [data-slate-editor] > :first-child': { marginTop: 0 },
        '& > :last-child, & [data-slate-editor] > :last-child': { marginBottom: 0 },
      }
    : {}),
  '& h1': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '1.75em',
    fontWeight: '600',
    color: options?.baseHeadingColor || 'fg',
    paddingY: '0.75em',
    ...(overrides?.h1 || {}),
  },
  '& h2': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '1.5em',
    fontWeight: '600',
    color: options?.baseHeadingColor || 'fg',
    paddingY: '0.75em',
    ...(overrides?.h2 || {}),
  },
  '& h3': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '1.25em',
    color: options?.baseHeadingColor || 'fg',
    fontWeight: '600',
    paddingY: '0.75em',
    ...(overrides?.h3 || {}),
  },
  '& h4': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '1.125em',
    fontWeight: '600',
    color: options?.baseHeadingColor || 'fg',
    paddingY: '0.75em',
    ...(overrides?.h4 || {}),
  },
  '& h5': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '1em',
    fontWeight: '600',
    color: options?.baseHeadingColor || 'fg',
    paddingY: '0.75em',
    ...(overrides?.h5 || {}),
  },
  '& h6': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '1em',
    fontWeight: '600',
    color: options?.baseHeadingColor || 'fg',
    paddingY: '0.75em',
    ...(overrides?.h6 || {}),
  },
  '& hr': {
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    marginBottom: '1rem',
  },
  '& ol': {
    listStyleType: 'decimal',
    fontSize: '1em',
    paddingY: 0,
    ...(overrides?.ol || {}),
  },
  '& ul': {
    listStyleType: 'disc',
    fontSize: '1em',
    paddingY: 0,
    ...(overrides?.ul || {}),
  },
  '& li': {
    fontSize: '1em',
    marginLeft: '1.25em',
    color: options?.baseTextColor || 'textPrimary',
    paddingY: '0.5em',
    paddingLeft: '0em',
    lineHeight: '1.8',
    ...(overrides?.li || {}),
  },
  '& li p': {
    padding: 0,
  },
  '& p': {
    color: options?.baseTextColor || 'textPrimary',
    fontSize: '1em',
    lineHeight: '1.8',
    padding: '0.8em 0',
    margin: 0,
    ...(overrides?.p || {}),
  },
  '& strong': {
    fontWeight: 'bold',
    color: options?.baseHeadingColor || 'fg',
    ...(overrides?.strong || {}),
  },
  '& img': {
    borderRadius: 'xl',
    display: 'inline-block',
    margin: '1em auto',
    maxWidth: '100%',
    ...(overrides?.img || {}),
  },
  '& quote': {
    backgroundColor: 'rgba(0,0,0,1)',
    padding: '1em',
    textAlign: 'center',
    ...(overrides?.quote || {}),
  },
  '& blockquote': {
    backgroundColor: 'rgba(255,255,255,.05)',
    borderLeft: '6px solid rgba(255,255,255,.05)',
    display: 'block',
    padding: '1em',
    marginY: '1em',
    ...(overrides?.blockquote || {}),
  },
  '& cite': {
    display: 'inline-block',
    fontSize: '0.8em',
    marginBottom: '1em',
    ...(overrides?.cite || {}),
  },
  '& a': {
    color: 'actionActive',
    fontWeight: 'bold',
    transition: 'all 0.2s ease-in',
    display: 'inline-block',
    textDecoration: 'underline',
    wordBreak: 'break-word',
    hyphens: 'auto',
    _hover: {
      color: 'fg',
      textDecoration: 'underline',
    },
    ...(overrides?.a || {}),
  },
  '& table': {
    borderCollapse: 'separate',
    borderRadius: '0.6em',
    borderSpacing: '1px',
    fontFamily: 'poppins',
    fontSize: '0.875em',
    fontWeight: '500',
    overflow: 'clip',
    marginY: '1em',
    textAlign: 'left',
    width: 'full',
    wordBreak: 'keep-all',
    ...(overrides?.table || {}),
  },
  '& th': {
    bgColor: 'primaryAlpha.400',
    paddingX: '4',
    paddingY: '3',
    fontFamily: 'poppins',
    fontSize: '0.7em',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 'widest',
    ...(overrides?.th || {}),
  },
  '& td': {
    bgColor: 'primaryAlpha.200',
    padding: '0.5em 1em',
    marginBottom: '20px',
    ...(overrides?.td || {}),
  },
})

export const presets: any = {
  ugc: {
    typographyStyles: {
      blockGap: CHAT_BLOCK_GAP,
      h1: {
        paddingY: 0,
      },
      h2: {
        paddingY: 0,
      },
      h3: {
        paddingY: 0,
      },
      h4: {
        paddingY: 0,
      },
      h5: {
        paddingY: 0,
      },
      h6: {
        paddingY: 0,
      },
      p: {
        lineHeight: '1.5',
        paddingY: '0.125em',
        margin: 0,
      },
      li: {
        paddingY: 0,
      },
      'blockquote > p': {
        paddingY: 0,
      },
      a: {
        color: 'actionActive',
        display: 'inline-block',

        textDecoration: 'underline',
        wordBreak: 'break-word',
        hyphens: 'auto',
      },
    },
  },
  request: {
    typographyStyles: {
      blockGap: CHAT_BLOCK_GAP,
      h1: {
        paddingY: 0,
      },
      h2: {
        paddingY: 0,
      },
      h3: {
        paddingY: 0,
      },
      h4: {
        paddingY: 0,
      },
      h5: {
        paddingY: 0,
      },
      h6: {
        paddingY: 0,
      },
      p: {
        lineHeight: '1.5',
        paddingY: '0.125em',
        margin: 0,
        opacity: 0.8,
      },
      li: {
        paddingY: 0,
      },
      'blockquote > p': {
        paddingY: 0,
      },
      a: {
        color: 'actionActive',
        display: 'inline-block',

        textDecoration: 'underline',
        wordBreak: 'break-word',
        hyphens: 'auto',
      },
    },
  },
  'chat-message': {
    typographyStyles: {
      blockGap: CHAT_BLOCK_GAP,
      h1: {
        paddingY: '0.25em',
        marginY: 0,
      },
      h2: {
        paddingY: '0.25em',
        marginY: 0,
      },
      h3: {
        paddingY: '0.25em',
        marginY: 0,
      },
      h4: {
        paddingY: '0.25em',
        marginY: 0,
      },
      h5: {
        paddingY: '0.25em',
        marginY: 0,
      },
      h6: {
        paddingY: '0.25em',
        marginY: 0,
      },
      p: {
        lineHeight: '1.5',
        paddingY: '0',
        marginY: 0,
        _first: {
          paddingTop: 0,
        },
        _last: {
          padding: 0,
        },
      },
      'ul, ol': {
        paddingY: '0.25em',
      },
      ul: {
        marginY: '0.75em',
      },
      ol: {
        marginY: '0.75em',
      },
      li: {
        paddingY: 0,
      },
      'blockquote > p': {
        paddingY: '0.25em',
      },
    },
    roles: {
      user: {
        baseTextColor: 'fg',
      },
      assistant: {
        baseTextColor: 'colorPalette.fg/80',
      },
    },
  },
  transcript: {
    typographyStyles: {
      p: {
        padding: 0,
        paddingBottom: 5,
      },
    },
  },
  article: {
    typographyStyles: {
      p: {
        padding: 0,
        paddingBottom: '1.5em',
        lineHeight: '1.67em',
        fontSize: {
          base: '1em',
          md: '1em',
          lg: '1.125em',
          xl: '1.125em',
        },
      },
      ol: {
        paddingBottom: '2em',
        marginLeft: '1em',
        fontSize: {
          base: '0.875em',
          md: '1em',
          lg: '1.125em',
          xl: '1.125em',
        },
      },
      ul: {
        paddingBottom: '1em',
        marginLeft: '1em',
      },
      li: {
        padding: 0,
        paddingBottom: '1em',
      },
      a: {
        color: 'orange.400',
        display: 'inline-block',
        lineHeight: 1.5,
        borderBottomWidth: '1px',
        borderBottomStyle: 'dotted',
        borderBottomColor: 'orange.300',
        fontWeight: 'bold',
        transition: 'all 0.2s ease-in',
        textDecoration: 'none',
        _hover: {
          color: 'orange.500',
          textDecoration: 'none',
          borderBottomStyle: 'solid',
        },
      },
      img: {
        borderRadius: 'xl',
        display: 'inline-block',
        margin: '1em auto',
        marginBottom: '2em',
        width: '100%',
      },
    },
  },
  edu: {
    typographyStyles: {
      p: {
        fontSize: {
          base: '1em',
          sm: '1.125em',
        },
        '& strong': {
          color: 'fg',
        },
      },
      li: {
        fontSize: {
          base: '1em',
          sm: '1.125em',
        },
        '& strong': {
          color: 'fg',
        },
      },
      ol: {
        padding: '0.5em',
        marginLeft: '1em',
      },
      ul: {
        padding: '0.5em',
        marginLeft: '1em',
      },

      a: {
        color: 'actionActive',
        fontWeight: 'bold',
        transition: 'all 0.2s ease-in',
        _hover: {
          color: 'fg',
          textDecoration: 'underline',
        },
        '& strong': {
          color: 'actionActive',
          textDecoration: 'none',
          fontWeight: 'bold',
          transition: 'all 0.2s ease-in',
          _hover: {
            color: 'fg',
            textDecoration: 'underline',
          },
        },
      },
      img: {
        borderRadius: 'xl',
        display: 'inline-block',
        margin: '1em auto',
        marginBottom: '2em',
        width: '100%',
      },
    },
  },
}
